// ======================================================================
// File: src/modules/ppe/components/boq/BOQLinesTable.js
// ======================================================================

import React, { useEffect, useState } from "react";
import ppeOrderService from "../../services/ppeOrderService";
import axiosClient from "../../../../api/axiosClient";

export default function BOQLinesTable({ lines, boqId, canEdit = true, onReload }) {
  // ------------------------------------------------------------
  // Local state
  // ------------------------------------------------------------
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingLineId, setEditingLineId] = useState(null);

  // Master data
  const [rawMaterials, setRawMaterials] = useState([]);
  const [boughtOutItems, setBoughtOutItems] = useState([]);

  const [form, setForm] = useState({
    material_type: "RAW",
    raw_material_id: "",
    bought_out_item_id: "",
    uom: "",
    quantity: "",
    scrap_percent: "",
    remarks: "",
  });

  const [editForm, setEditForm] = useState({
    quantity: "",
    scrap_percent: "",
    remarks: "",
  });

  // ------------------------------------------------------------
  // Load masters
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadMasters() {
      try {
        const [rawRes, boughtRes] = await Promise.all([
          axiosClient.get("/api/ppe/masters/raw-materials"),
          axiosClient.get("/api/ppe/masters/boughtout-items"),
        ]);
        setRawMaterials(rawRes.data?.data || []);
        setBoughtOutItems(boughtRes.data?.data || []);
      } catch {}
    }
    loadMasters();
  }, []);

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  function resolveMaterialName(line) {
    if (line.material_type === "RAW") {
      const m = rawMaterials.find(r => r.id === line.raw_material_id);
      return m ? `${m.name} (${m.code})` : `ID: ${line.raw_material_id}`;
    }
    const b = boughtOutItems.find(i => i.id === line.bought_out_item_id);
    return b ? `${b.name} (${b.code})` : `ID: ${line.bought_out_item_id}`;
  }

  function resolveUOM(uomId) {
    // try RAW materials
    const raw = rawMaterials.find(r => r.uom === uomId);
    if (raw) return raw.uom_code || raw.uom_name || raw.uom;

    // try Bought Out
    const bought = boughtOutItems.find(b => b.uom === uomId);
    if (bought) return bought.uom_code || bought.uom_name || bought.uom;

    return uomId || "-";
  }

  function handleMaterialSelect(type, id) {
    if (type === "RAW") {
      const m = rawMaterials.find(r => r.id === Number(id));
      setForm({
        ...form,
        raw_material_id: id,
        uom: m?.uom || "",
      });
    } else {
      const b = boughtOutItems.find(i => i.id === Number(id));
      setForm({
        ...form,
        bought_out_item_id: id,
        uom: b?.uom || "",
      });
    }
  }

  // ------------------------------------------------------------
  // Add Line
  // ------------------------------------------------------------
  async function handleAddLine() {
    if (!boqId || !canEdit) return;

    setSaving(true);
    try {
      await ppeOrderService.createBOQLine(boqId, {
        material_type: form.material_type,
        raw_material_id:
          form.material_type === "RAW" ? form.raw_material_id : null,
        bought_out_item_id:
          form.material_type === "BOUGHT_OUT"
            ? form.bought_out_item_id
            : null,
        uom: form.uom,
        quantity: Number(form.quantity),
        scrap_percent: Number(form.scrap_percent || 0),
        remarks: form.remarks,
      });

      setForm({
        material_type: "RAW",
        raw_material_id: "",
        bought_out_item_id: "",
        uom: "",
        quantity: "",
        scrap_percent: "",
        remarks: "",
      });

      setAdding(false);
      onReload && onReload();
    } finally {
      setSaving(false);
    }
  }

  // ------------------------------------------------------------
  // Edit Line
  // ------------------------------------------------------------
  function startEdit(line) {
    if (!canEdit) return;

    setEditingLineId(line.id);
    setEditForm({
      quantity: line.quantity,
      scrap_percent: line.scrap_percent || 0,
      remarks: line.remarks || "",
    });
  }

  async function saveEdit(lineId) {
    if (!canEdit) return;

    setSaving(true);
    try {
      await ppeOrderService.updateBOQLine(lineId, {
        quantity: Number(editForm.quantity),
        scrap_percent: Number(editForm.scrap_percent || 0),
        remarks: editForm.remarks,
      });
      setEditingLineId(null);
      onReload && onReload();
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    setEditingLineId(null);
  }

  async function handleDelete(lineId) {
    if (!canEdit) return;

    if (!window.confirm("Delete this BOQ line?")) return;
    await ppeOrderService.deleteBOQLine(lineId);
    onReload && onReload();
  }

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <div>
      {/* ================= ADD LINE ================= */}
      {boqId && canEdit && (
        <div className="mb-2">
          {!adding && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setAdding(true)}
            >
              + Add BOQ Line
            </button>
          )}

          {adding && (
            <div className="border rounded p-2 mt-2">
              <div className="row g-2">
                <div className="col-md-2">
                  <select
                    className="form-select form-select-sm"
                    value={form.material_type}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        material_type: e.target.value,
                        raw_material_id: "",
                        bought_out_item_id: "",
                        uom: "",
                      })
                    }
                  >
                    <option value="RAW">Raw Material</option>
                    <option value="BOUGHT_OUT">Bought Out</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <select
                    className="form-select form-select-sm"
                    value={
                      form.material_type === "RAW"
                        ? form.raw_material_id
                        : form.bought_out_item_id
                    }
                    onChange={(e) =>
                      handleMaterialSelect(form.material_type, e.target.value)
                    }
                  >
                    <option value="">Select Material</option>
                    {(form.material_type === "RAW"
                      ? rawMaterials
                      : boughtOutItems
                    ).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name} ({m.code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <input
                    className="form-control form-control-sm"
                    placeholder="UOM"
                    value={form.uom}
                    readOnly
                  />
                </div>

                <div className="col-md-2">
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder="Qty"
                    value={form.quantity}
                    onChange={(e) =>
                      setForm({ ...form, quantity: e.target.value })
                    }
                  />
                </div>

                <div className="col-md-3 d-flex gap-1">
                  <button
                    className="btn btn-sm btn-success"
                    disabled={saving}
                    onClick={handleAddLine}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setAdding(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================= TABLE ================= */}
      <table className="table table-bordered table-sm">
        <thead className="table-light">
          <tr>
            <th>Material</th>
            <th>UOM</th>
            <th>Qty</th>
            <th>Scrap %</th>
            <th>Remarks</th>
            <th style={{ width: 120 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr>
              <td colSpan="6" className="text-center text-muted">
                No BOQ lines added
              </td>
            </tr>
          )}

          {lines.map((l) => (
            <tr key={l.id}>
              <td>{resolveMaterialName(l)}</td>
              <td>{l.uom_code || "-"}</td>

              {editingLineId === l.id ? (
                <>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.quantity}
                      onChange={(e) =>
                        setEditForm({ ...editForm, quantity: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={editForm.scrap_percent}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          scrap_percent: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={editForm.remarks}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          remarks: e.target.value,
                        })
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-success me-1"
                      onClick={() => saveEdit(l.id)}
                      disabled={saving}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{l.quantity}</td>
                  <td>{l.scrap_percent || 0}</td>
                  <td>{l.remarks || "-"}</td>
                  <td>
                    {canEdit && (
                      <>
                        <button
                          className="btn btn-sm btn-outline-primary me-1"
                          onClick={() => startEdit(l)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(l.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
